import {
	DEFAULT_CONFS,
	DEFAULT_DIVS,
	DEFAULT_STADIUM_CAPACITY,
} from "./constants.ts";
import type {
	GameAttributeKey,
	GameAttributesLeagueWithHistory,
	GameAttributeWithHistory,
} from "./types.ts";

const wrap = <T>(value: T): GameAttributeWithHistory<T> => [
	{
		start: -Infinity,
		value,
	},
];

// gameAttributes is mixed up between league settings, game state, teams, and cache
export const gameAttributesKeysGameState: GameAttributeKey[] = [
	"phase",
	"nextPhase",
	"gameOver",
	"otherTeamsWantToHire",
	"lowestDifficulty",
	"difficulty",
	"gracePeriodEnd",
	"lid",
	"userTid",
	"userTids",
	"season",
	"startingSeason",
	"numDraftPicksCurrent",
	"expansionDraft",
	"autoRelocate",
	"autoExpand",
];
export const gameAttributesKeysTeams: GameAttributeKey[] = ["confs", "divs"];
export const gameAttributesCache: GameAttributeKey[] = [
	"numTeams",
	"numActiveTeams",
	"teamInfoCache",
];

const gameAttributesKeysSportSpecific = {
	baseball: new Set<GameAttributeKey>([
		"dh",
		"foulFactor",
		"groundFactor",
		"lineFactor",
		"flyFactor",
		"powerFactor",
		"stealFactor",
		"throwOutFactor",
		"strikeFactor",
		"balkFactor",
		"wildPitchFactor",
		"passedBallFactor",
		"hitByPitchFactor",
		"swingFactor",
		"contactFactor",
		"hitFactor",
		"errorFactor",
	]),
	basketball: new Set<GameAttributeKey>([
		"threePointers",
		"threePointTendencyFactor",
		"threePointAccuracyFactor",
		"twoPointAccuracyFactor",
		"ftAccuracyFactor",
		"blockFactor",
		"stealFactor",
		"turnoverFactor",
		"orbFactor",
		"assistFactor",
		"realPlayerDeterminism",
		"rpdPot",
		"foulRateFactor",
		"foulsNeededToFoulOut",
		"foulsUntilBonus",
		"elam",
		"elamASG",
		"elamMinutes",
		"elamOvertime",
		"elamPoints",
		"randomDebutsForever",
		"realDraftRatings",
		"numPlayersDunk",
		"numPlayersThree",
		"quarterLength",
		"overtimeLength",
		"overtimeLengthPlayoffs",
		"numPlayersOnCourt",
		"pace",
		"allStarDunk",
		"allStarThree",
		"forceRetireRealPlayers",
		"forceHistoricalRosters",
	]),
	football: new Set<GameAttributeKey>([
		"fantasyPoints",
		"foulRateFactor",
		"quarterLength",
		"overtimeLength",
		"overtimeLengthPlayoffs",
		"passFactor",
		"rushYdsFactor",
		"passYdsFactor",
		"completionFactor",
		"scrambleFactor",
		"sackFactor",
		"fumbleFactor",
		"intFactor",
		"fgAccuracyFactor",
		"fourthDownFactor",
		"onsideFactor",
		"onsideRecoveryFactor",
		"pace",
		"scrimmageTouchbackKickoff",
		"twoPointConversions",
		"footballOvertime",
		"footballOvertimePlayoffs",
	]),
	hockey: new Set<GameAttributeKey>([
		"foulRateFactor",
		"quarterLength",
		"overtimeLength",
		"overtimeLengthPlayoffs",
		"hitFactor",
		"giveawayFactor",
		"takeawayFactor",
		"blockFactor",
		"deflectionFactor",
		"saveFactor",
		"assistFactor",
		"pace",
	]),
};
export const gameAttributesKeysOtherSports = new Set<GameAttributeKey>();
for (const [sport, keys] of Object.entries(gameAttributesKeysSportSpecific)) {
	if (sport !== process.env.SPORT) {
		for (const key of keys) {
			if (!gameAttributesKeysSportSpecific[process.env.SPORT].has(key)) {
				gameAttributesKeysOtherSports.add(key);
			}
		}
	}
}

const defaultGameAttributes: GameAttributesLeagueWithHistory = {
	phase: 0,
	nextPhase: undefined, // Used only for fantasy draft
	playerBioInfo: undefined,
	injuries: undefined,
	tragicDeaths: undefined,
	daysLeft: 0, // Used only for free agency
	gameOver: false,
	salaryCap: 150000, // [thousands of dollars]
	minPayroll: 95000, // [thousands of dollars]
	luxuryPayroll: 168000, // [thousands of dollars]
	luxuryTax: 1.5,
	minContract: 1200, // [thousands of dollars]
	maxContract: 50000, // [thousands of dollars]
	minContractLength: 1,
	maxContractLength: 5,
	minRosterSize: 10,
	maxRosterSize: 15,
	softCapTradeSalaryMatch: 125,
	numGames: wrap(82), // per season
	numGamesDiv: 16,
	numGamesConf: 36,
	otherTeamsWantToHire: false,
	numPeriods: 4, // per game
	quarterLength: 12, // [minutes]
	overtimeLength: 5,
	overtimeLengthPlayoffs: null,
	confs: wrap(DEFAULT_CONFS),
	divs: wrap(DEFAULT_DIVS),
	numGamesPlayoffSeries: wrap([7, 7, 7, 7]),
	numPlayoffByes: wrap(0),
	aiTradesFactor: 1,
	stopOnInjury: false,
	stopOnInjuryGames: 20,
	// According to data/injuries.ods, 0.25 injuries occur every game. Divided over 10 players and ~200 possessions, that means each player on the court has P = 0.25 / 10 / 200 = 0.000125 probability of being injured this play.
	injuryRate: 0.25 / 10 / 200,
	homeCourtAdvantage: 1,
	// The tragic death rate is the probability that a player will die a tragic death on a given regular season day. Yes, this only happens in the regular season. With roughly 100 days in a season, the default is about one death every 50 years.
	tragicDeathRate: 1 / (100 * 50),
	// The probability that a new player will be the son or brother of an existing player. In practice, the observed number may be smaller than this because sometimes a valid match will not be found.
	sonRate: 0.02,
	brotherRate: 0.02,
	forceRetireAge: 0,
	forceRetireSeasons: 0,
	minRetireAge: 26,
	groupScheduleSeries: false,

	salaryCapType: "soft",

	maxOvertimes: wrap(null),
	shootoutRounds: wrap(0),
	maxOvertimesPlayoffs: null,
	shootoutRoundsPlayoffs: 0,

	otl: wrap(false),

	draftType: "nba2019",
	draftLotteryCustomChances: [
		140, 140, 140, 125, 105, 90, 75, 60, 45, 30, 20, 15, 10, 5,
	],
	draftLotteryCustomNumPicks: 4,
	numDraftRounds: 2,
	draftAges: [19, 22],
	defaultStadiumCapacity: DEFAULT_STADIUM_CAPACITY,
	playersRefuseToNegotiate: true,
	allStarGame: 0.7,
	allStarNum: 12,
	allStarType: "byConf",
	allStarDunk: true,
	allStarThree: true,
	budget: true,
	numSeasonsFutureDraftPicks: 4,
	foulRateFactor: 1,
	foulsNeededToFoulOut: 6,
	foulsUntilBonus: [5, 4, 2],
	rookieContractLengths: [3, 2],
	rookiesCanRefuse: true,

	pace: 100,
	threePointers: true,
	threePointTendencyFactor: 1,
	threePointAccuracyFactor: 1,
	twoPointAccuracyFactor: 1,
	ftAccuracyFactor: 1,
	blockFactor: 1,
	stealFactor: 1,
	turnoverFactor: 1,
	orbFactor: 1,
	expansionDraft: { phase: "setup" },

	challengeNoDraftPicks: false,
	challengeNoFreeAgents: false,
	challengeNoRatings: false,
	challengeNoTrades: false,
	challengeLoseBestPlayer: false,
	challengeFiredLuxuryTax: false,
	challengeFiredMissPlayoffs: false,
	challengeSisyphusMode: false,
	challengeThanosMode: 0,
	thanosCooldownEnd: undefined,
	repeatSeason: undefined,
	equalizeRegions: false,
	realPlayerDeterminism: 0,
	spectator: false,
	elam: false,
	elamASG: false,
	elamMinutes: 4,
	elamOvertime: false,
	elamPoints: 8,
	playerMoodTraits: true,
	numPlayersOnCourt: 5,
	aiJerseyRetirement: true,
	tiebreakers: wrap([
		"headToHeadRecord",
		"divWinner",
		"divRecordIfSame",
		"confRecordIfSame",
		"marginOfVictory",
		"coinFlip",
	]),
	hofFactor: 1,
	tradeDeadline: 0.6,
	pointsFormula: wrap(""),
	randomDebutsForever: undefined,
	realDraftRatings: undefined,
	hideDisabledTeams: false,
	goatFormula: undefined,
	goatSeasonFormula: undefined,
	inflationAvg: 0,
	inflationMax: 0,
	inflationMin: 0,
	inflationStd: 0,
	riggedLottery: undefined,
	numDraftPicksCurrent: undefined,
	playoffsByConf: true,
	playoffsNumTeamsDiv: wrap(0),
	playoffsReseed: false,
	playIn: true,
	numPlayersDunk: 4,
	numPlayersThree: 8,
	draftPickAutoContract: true,
	draftPickAutoContractPercent: 25,
	draftPickAutoContractRounds: 1,
	dh: "all",
	gender: "male",
	heightFactor: 1,
	weightFactor: 1,
	numWatchColors: 1,
	autoExpand: undefined,
	autoExpandProb: 0,
	autoExpandNumTeams: 2,
	autoExpandMaxNumTeams: 40,
	autoExpandGeo: "naFirst",
	autoRelocate: undefined,
	autoRelocateProb: 0,
	autoRelocateGeo: "naFirst",
	autoRelocateRebrand: true,
	autoRelocateRealign: true,
	alwaysShowCountry: false,

	// These will always be overwritten when creating a league, just here for TypeScript
	lid: 0,
	userTid: [
		{
			start: -Infinity,
			value: 0,
		},
	],
	userTids: [0],
	season: 0,
	startingSeason: 0,
	teamInfoCache: [],
	gracePeriodEnd: 0,
	numTeams: 0,
	numActiveTeams: 0,
	difficulty: 0, // See constants.DIFFICULTY for values
	lowestDifficulty: 0,
	fantasyPoints: undefined,

	// These are only for FBGM, but for TypeScript define them here
	passFactor: 1,
	rushYdsFactor: 1,
	passYdsFactor: 1,
	completionFactor: 1,
	scrambleFactor: 1,
	sackFactor: 1,
	fumbleFactor: 1,
	intFactor: 1,
	fgAccuracyFactor: 1,
	fourthDownFactor: 1,
	onsideFactor: 1,
	onsideRecoveryFactor: 1,
	scrimmageTouchbackKickoff: 35,
	twoPointConversions: true,
	footballOvertime: "bothPossess",
	footballOvertimePlayoffs: "bothPossess",

	// These are only for ZGMH, but for TypeScript define them here
	hitFactor: 1,
	giveawayFactor: 1,
	takeawayFactor: 1,
	deflectionFactor: 1,
	saveFactor: 1,
	assistFactor: 1,

	// These are only for ZGMB, but for TypeScript define them here
	foulFactor: 1,
	groundFactor: 1,
	lineFactor: 1,
	flyFactor: 1,
	powerFactor: 1,
	throwOutFactor: 1,
	strikeFactor: 1,
	balkFactor: 1,
	wildPitchFactor: 1,
	passedBallFactor: 1,
	hitByPitchFactor: 1,
	swingFactor: 1,
	contactFactor: 1,
	errorFactor: 1,

	neutralSite: "never",
	rpdPot: true,
	saveOldBoxScores: {
		pastSeasons: 2,
		pastSeasonsType: "all",
	},
	currencyFormat: ["$", ".", ""],
	forceRetireRealPlayers: false,
	forceHistoricalRosters: false,
};

export default defaultGameAttributes;
